import {
  GameState,
  GameStateDelta,
  LineupEntry,
  TagDataIngest,
} from "@aura-frontend/omnibus-az-client";

import {
  taggedPlayerToLineupEntry,
  toLineupPlayerName,
} from "../api/omnibusMappers";
import { TeamSide } from "../constants/teams";
import {
  Activity,
  GameFullResponse,
  GamePlayerRef,
  LineUpPlayer,
  ResultingState,
  TaggedPlayer,
} from "../types/gameFull";
import {
  GameStateEdit,
  getStateBeforePitch,
  toBaserunnerFlags,
} from "./pitchTagging";
import { getActivitiesForPA, getLastTaggedPitch } from "./plateAppearanceFeed";
import {
  comparePlateAppearances,
  getLivePaOfInning,
  getPaOfInningFromId,
} from "./plateAppearanceId";
import { findLineupPlayer } from "./playerChange";

/**
 * Rebuilds the omnibus /data/tag payload for a pitch from game service data,
 * so a past pitch can be re-tagged (e.g. a new hitter for its PA) without
 * the original omnibus tag. Matches what the legacy tagger sends
 * (pages/Tagging/.../SubmissionButton.tsx handleUpdateEvent).
 */

/** Game service pitch results that omnibus names differently. */
const OMNIBUS_PITCH_RESULTS: Record<string, string> = {
  CalledStrike: "StrikeCalled",
  SwingingStrike: "StrikeSwinging",
  Ball: "BallCalled",
};

export const toOmnibusPitchResult = (pitchResult: string) =>
  OMNIBUS_PITCH_RESULTS[pitchResult] ?? pitchResult;

/**
 * Omnibus has no HitByPitch play result, and drops the whole metadata (pitch
 * result included) for one it doesn't know. Like the legacy tagger, an HBP
 * sends its pitch result only; the play result is just shown in the form.
 */
export const toOmnibusPlayResult = (playResult: string | null) =>
  playResult === "HitByPitch" ? null : playResult;

/**
 * Not a Pitch, as the mobile app sends it: omnibus has no NotAPitch pitch
 * result (and drops the whole metadata for one it doesn't know), and the game
 * service reads this event type as its NotAPitch. The tag still carries the
 * pitch's game state (the game service ignores it on a tag with metadata), so
 * the Edit Event Log shows it in its inning.
 */
const NOT_A_PITCH_METADATA = { event_type: "not_of_interest" };

export type TagPlayerOverrides = {
  hitter?: LineUpPlayer;
  pitcher?: LineUpPlayer;
};

const lineUpPlayerToTagged = (player: LineUpPlayer): TaggedPlayer => ({
  externalId: player.playerId,
  name: toLineupPlayerName(player),
  battingOrder: player.battingOrder,
  jerseyNumber: player.jerseyNumber,
  hitSide: player.hitSide,
  pitchSide: player.pitchSide,
});

/**
 * Override, else the pitch's tag, else the PA's player with lineup details
 * from `side`, the team they play for in that half-inning.
 */
const resolveTaggedPlayer = (
  data: GameFullResponse,
  override: LineUpPlayer | undefined,
  tagged: TaggedPlayer | null | undefined,
  paRef: GamePlayerRef | undefined,
  side: TeamSide,
): TaggedPlayer | undefined => {
  if (override) return lineUpPlayerToTagged(override);
  if (tagged?.externalId || tagged?.name) return tagged;
  if (!paRef) return undefined;
  const lineupPlayer = findLineupPlayer(data, paRef.playerId, side);
  return lineupPlayer
    ? lineUpPlayerToTagged(lineupPlayer)
    : { externalId: paRef.playerId, name: paRef.playerName };
};

/**
 * 1-based order of the PA within its half-inning, from its id; counted from
 * the half's loaded PAs only for an id not in the service's format.
 */
const getPaOfInning = (data: GameFullResponse, plateAppearanceId: string) => {
  const fromId = getPaOfInningFromId(plateAppearanceId);
  if (fromId) return fromId;
  const pa = data.plateAppearances.find(
    (p) => p.plateAppearanceId === plateAppearanceId,
  );
  if (!pa) return 1;
  const halfPAs = data.plateAppearances
    .filter(
      (p) => p.inning === pa.inning && p.isTopOfInning === pa.isTopOfInning,
    )
    .sort(comparePlateAppearances);
  return (
    halfPAs.findIndex((p) => p.plateAppearanceId === plateAppearanceId) + 1
  );
};

/**
 * Pitches of a PA, leaving out Not a Pitch tags (they aren't game pitches),
 * up to `before` when given. A tag's pitchOfPa is this count plus one.
 */
const countGamePitches = (
  data: GameFullResponse,
  plateAppearanceId: string,
  before?: Activity,
) => {
  const activities = getActivitiesForPA(data, plateAppearanceId);
  const end = before
    ? activities.findIndex((a) => a.activityId === before.activityId)
    : activities.length;
  return activities
    .slice(0, end)
    .filter((a) => a.result?.pitchResult !== "NotAPitch").length;
};

/**
 * The game state going into a pitch, with its players (overrides first),
 * as a pitch's /data/tag carries it. Null if the pitch has no state.
 */
const buildPitchGameState = (
  data: GameFullResponse,
  activity: Activity,
  overrides: TagPlayerOverrides,
  stateBefore: ResultingState | null,
): GameState | null => {
  const pa = data.plateAppearances.find(
    (p) => p.plateAppearanceId === activity.plateAppearanceId,
  );
  if (!stateBefore) return null;

  const battingSide = stateBefore.isTopOfInning ? "away" : "home";
  const fieldingSide = stateBefore.isTopOfInning ? "home" : "away";
  const hitter = resolveTaggedPlayer(
    data,
    overrides.hitter,
    activity.playerTag?.hitter,
    pa?.hitter,
    battingSide,
  );
  const pitcher = resolveTaggedPlayer(
    data,
    overrides.pitcher,
    activity.playerTag?.pitcher,
    pa?.pitcher,
    fieldingSide,
  );
  const hitterSlot =
    hitter?.battingOrder ??
    findLineupPlayer(data, hitter?.externalId, battingSide)?.battingOrder;

  // The game service doesn't track a catcher per pitch: use the fielding
  // team's lineup catcher
  const catcher = data.lineUp[fieldingSide].find(
    (p) => p.position?.toUpperCase() === "C",
  );

  return {
    paOfInning: getPaOfInning(data, activity.plateAppearanceId),
    pitchOfPa: countGamePitches(data, activity.plateAppearanceId, activity) + 1,
    awayScore: stateBefore.score.away,
    homeScore: stateBefore.score.home,
    inning: {
      integer: stateBefore.inning,
      half: stateBefore.isTopOfInning ? "top" : "bottom",
    },
    count: { balls: stateBefore.balls, strikes: stateBefore.strikes },
    outs: stateBefore.outs,
    // The omnibus CSV's preOccupiedBases; the game service ignores a pitch
    // tag's gameState and keeps its own runners
    occupiedBases: toBaserunnerFlags(stateBefore.baseRunners),
    ...(hitter && {
      // Like the legacy tagger, a hitter without a slot goes without one
      currentHitter: taggedPlayerToLineupEntry(
        hitter,
        hitterSlot as LineupEntry["position"],
      ),
    }),
    ...(pitcher && {
      currentPitcher: taggedPlayerToLineupEntry(pitcher, "pitcher"),
    }),
    ...(catcher && {
      currentCatcher: taggedPlayerToLineupEntry(
        lineUpPlayerToTagged(catcher),
        "catcher",
      ),
    }),
  };
};

/**
 * Builds the /data/tag body for a tagged pitch, with optional hitter/pitcher
 * overrides. Returns null for untagged pitches (nothing to re-send).
 *
 * `machineUuid` is the SmartPark being tagged, not the pitch's: a field's
 * game takes pitches from all its machines, and omnibus only checks the
 * user can tag on the one sent (like the V1 tagger, which sends its own).
 *
 * `stateBefore` is the state the pitch was thrown into. Once the pitch is
 * tagged locally, `data` can only carry it over from the pitch before, which
 * misses Edit Live Game State edits, so a new save passes it in.
 */
export const buildTagPayload = (
  data: GameFullResponse,
  activity: Activity,
  machineUuid: string,
  overrides: TagPlayerOverrides = {},
  stateBefore: ResultingState | null = getStateBeforePitch(data, activity),
): TagDataIngest | null => {
  const result = activity.result;
  if (!result?.pitchResult) return null;

  const gameState = buildPitchGameState(data, activity, overrides, stateBefore);

  if (result.pitchResult === "NotAPitch") {
    return {
      payload: {
        event_uuid: activity.activityId,
        event_time: activity.pitch.eventTime,
        machine_uuid: machineUuid,
        tag_data: {
          metadata: NOT_A_PITCH_METADATA,
          ...(gameState && { gameState }),
        },
      },
    } as unknown as TagDataIngest;
  }

  if (!gameState) return null;

  const metadata = {
    pitchResult: toOmnibusPitchResult(result.pitchResult),
    playResult: toOmnibusPlayResult(result.playResult),
    outsRecorded: activity.resultingState?.outsRecorded ?? 0,
    runsScored: activity.resultingState?.runsScored ?? 0,
    pitchType: result.pitchType,
    contactType: result.contactType,
    prePitchStance: result.pitcherSet,
    // The field the game service (and the mobile app) use
    ...(result.occupiedBases
      ? { postTagOccupiedBases: result.occupiedBases }
      : {}),
  } as GameStateDelta;

  return {
    payload: {
      event_uuid: activity.activityId,
      event_time: activity.pitch.eventTime,
      machine_uuid: machineUuid,
      tag_data: { metadata, gameState },
    },
  };
};

/**
 * A fill: a tag for an untagged pitch carrying only its game state and
 * players, so its omnibus CSV row has a hitter and pitcher. Null for tagged
 * pitches.
 *
 * Its metadata has no pitchResult, so the game service drops it as a pitch
 * tag with no outcome. `metadata: null` would instead make it a state tag,
 * which the game service applies to the live game, putting this old pitch's
 * count and hitter back. Omnibus accepts it and saves it as the pitch's tag.
 */
export const buildPlayerFillPayload = (
  data: GameFullResponse,
  activity: Activity,
  machineUuid: string,
  overrides: TagPlayerOverrides = {},
): TagDataIngest | null => {
  if (activity.result?.pitchResult) return null;

  const gameState = buildPitchGameState(
    data,
    activity,
    overrides,
    getStateBeforePitch(data, activity),
  );
  if (!gameState) return null;

  return {
    payload: {
      event_uuid: activity.activityId,
      event_time: activity.pitch.eventTime,
      machine_uuid: machineUuid,
      tag_data: {
        metadata: { outsRecorded: 0, runsScored: 0 } as GameStateDelta,
        gameState,
      },
    },
  };
};

/**
 * Payload re-tagging the last tagged pitch of a PA (see getLastTaggedPitch)
 * with the given hitter/pitcher, or null if none of its pitches is tagged.
 * Post At-Bat Update edits that pitch's results; the PA's other pitches get
 * the players through the tag queue.
 */
export const buildLastPitchRetag = (
  data: GameFullResponse,
  plateAppearanceId: string,
  machineUuid: string,
  overrides: TagPlayerOverrides,
): TagDataIngest | null => {
  const lastTagged = getLastTaggedPitch(data, plateAppearanceId);
  return lastTagged
    ? buildTagPayload(data, lastTagged, machineUuid, overrides)
    : null;
};

/**
 * The live hitter, pitcher and catcher for a tag with no pitch, with any
 * overrides. Omnibus keeps a tag's gameState as the game's current state and
 * the legacy tagger reads its current players from it, so a tag leaving one
 * out shows them as empty there. Naming players who already hold their spot
 * changes nothing in the game service: it only re-seats or substitutes when a
 * named player differs (win-game-service reconcileIdentity).
 */
const getLiveTagPlayers = (
  data: GameFullResponse,
  overrides: TagPlayerOverrides = {},
): Pick<GameState, "currentHitter" | "currentPitcher" | "currentCatcher"> => {
  const { game } = data;
  const battingSide = game.isTopOfInning ? "away" : "home";
  const fieldingSide = game.isTopOfInning ? "home" : "away";
  const hitter = resolveTaggedPlayer(
    data,
    overrides.hitter,
    undefined,
    game.currentHitter ?? undefined,
    battingSide,
  );
  const pitcher = resolveTaggedPlayer(
    data,
    overrides.pitcher,
    undefined,
    game.currentPitcher ?? undefined,
    fieldingSide,
  );
  const hitterSlot =
    hitter?.battingOrder ??
    findLineupPlayer(data, hitter?.externalId, battingSide)?.battingOrder;
  const catcher = data.lineUp[fieldingSide].find(
    (p) => p.position?.toUpperCase() === "C",
  );
  return {
    ...(hitter && {
      currentHitter: taggedPlayerToLineupEntry(
        hitter,
        hitterSlot as LineupEntry["position"],
      ),
    }),
    ...(pitcher && {
      currentPitcher: taggedPlayerToLineupEntry(pitcher, "pitcher"),
    }),
    ...(catcher && {
      currentCatcher: taggedPlayerToLineupEntry(
        lineUpPlayerToTagged(catcher),
        "catcher",
      ),
    }),
  };
};

/**
 * A state tag: a /data/tag with no pitch outcome (`metadata: null`) and the
 * corrected game state. Omnibus relays it to the game service as is, which
 * applies it to the game (StateTagged) and sends every client the new state.
 * No event_uuid: omnibus assigns one, and no pitch is being tagged.
 */
export const buildStateTagPayload = (
  data: GameFullResponse,
  machineUuid: string,
  edit: GameStateEdit,
): TagDataIngest => {
  const pa = data.plateAppearances.find(
    (p) => p.plateAppearanceId === data.game.currentPlateAppearanceId,
  );
  const gameState: GameState = {
    paOfInning: getLivePaOfInning(data.game),
    pitchOfPa: (pa ? countGamePitches(data, pa.plateAppearanceId) : 0) + 1,
    awayScore: edit.awayScore,
    homeScore: edit.homeScore,
    inning: {
      integer: edit.inning,
      half: edit.isTopInning ? "top" : "bottom",
    },
    count: { balls: edit.balls, strikes: edit.strikes },
    outs: edit.outs,
    occupiedBases: edit.baserunners,
    // In a new half the live players are the other clubs': the game service
    // seats that half's own, and the next pitch tag names them
    ...(edit.isTopInning === data.game.isTopOfInning &&
      getLiveTagPlayers(data)),
  };

  return {
    payload: {
      machine_uuid: machineUuid,
      event_time: Date.now() / 1000,
      tag_data: { metadata: null, gameState },
    },
  } as unknown as TagDataIngest;
};

/**
 * A player tag: a state tag naming the active at-bat's new hitter and/or
 * pitcher, so no pitch has to be tagged to change them. The game service
 * re-seats the current hitter/pitcher (subbing them into the lineup if
 * needed). The players it doesn't change are named as they are (see
 * getLiveTagPlayers). Its state is the live state, which the game service
 * skips as a no-op patch.
 */
export const buildPlayerTagPayload = (
  data: GameFullResponse,
  machineUuid: string,
  overrides: TagPlayerOverrides,
): TagDataIngest => {
  const { game } = data;
  const gameState: GameState = {
    paOfInning: getLivePaOfInning(game),
    pitchOfPa:
      (game.currentPlateAppearanceId
        ? countGamePitches(data, game.currentPlateAppearanceId)
        : 0) + 1,
    awayScore: game.score.away,
    homeScore: game.score.home,
    inning: {
      integer: game.inning,
      half: game.isTopOfInning ? "top" : "bottom",
    },
    count: { balls: game.balls, strikes: game.strikes },
    outs: game.outs,
    ...getLiveTagPlayers(data, overrides),
  };

  return {
    payload: {
      machine_uuid: machineUuid,
      event_time: Date.now() / 1000,
      tag_data: { metadata: null, gameState },
    },
  } as unknown as TagDataIngest;
};
